import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, AlertTriangle, Shield, Briefcase } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PrivacyConsent, HoneypotField } from '../components/ui/privacy-consent';
import { toast } from 'sonner';
import { contactsAPI, dpoContactAPI, jobApplicationsAPI, uploadAPI } from '../services/api';
import { ImageUpload } from '../components/ui/image-upload';

// Contact Form Component
const ContactForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      toast.error('Devi dichiarare di aver letto l\'informativa privacy per inviare il modulo');
      return;
    }
    setIsSubmitting(true);

    try {
      await contactsAPI.create({ ...formData, website: honeypot });
      toast.success('Messaggio inviato con successo!');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setPrivacyAccepted(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio messaggio');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div>
        <Label htmlFor="name">{t('contact.name')}</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="mt-2"
          data-testid="contact-name-input"
        />
      </div>
      
      <div>
        <Label htmlFor="email">{t('contact.email')}</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="mt-2"
          data-testid="contact-email-input"
        />
      </div>
      
      <div>
        <Label htmlFor="phone">{t('contact.phone')}</Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          className="mt-2"
          data-testid="contact-phone-input"
        />
      </div>
      
      <div>
        <Label htmlFor="message">{t('contact.message')}</Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          className="mt-2 resize-none"
          data-testid="contact-message-input"
        />
      </div>
      
      <PrivacyConsent
        checked={privacyAccepted}
        onChange={setPrivacyAccepted}
        testId="contact-privacy-checkbox"
      />

      <HoneypotField value={honeypot} onChange={setHoneypot} idPrefix="contact" />

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={isSubmitting || !privacyAccepted}
        data-testid="contact-submit-btn"
      >
        {isSubmitting ? 'Invio in corso...' : t('contact.send')}
        <Send className="ml-2 w-4 h-4" />
      </Button>
    </form>
  );
};

// DPO Contact Form
const DPOContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      toast.error('Devi dichiarare di aver letto l\'informativa privacy per inviare il modulo');
      return;
    }
    setIsSubmitting(true);

    try {
      await dpoContactAPI.create({ ...formData, website: honeypot });
      toast.success('Segnalazione inviata al DPO con successo!');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setPrivacyAccepted(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio segnalazione');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="dpo-form">
      <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-6">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-500">Contatto DPO</p>
            <p className="text-sm text-muted-foreground mt-1">
              Utilizza questo form per contattare il Responsabile della Protezione dei Dati per questioni relative alla privacy e al trattamento dei tuoi dati personali.
            </p>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="dpo-name">Nome e Cognome</Label>
        <Input
          id="dpo-name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="mt-2"
          data-testid="dpo-name-input"
        />
      </div>
      
      <div>
        <Label htmlFor="dpo-email">Email</Label>
        <Input
          id="dpo-email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="mt-2"
          data-testid="dpo-email-input"
        />
      </div>
      
      <div>
        <Label htmlFor="dpo-subject">Oggetto</Label>
        <Input
          id="dpo-subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          className="mt-2"
          placeholder="Es: Richiesta accesso dati, Cancellazione dati..."
          data-testid="dpo-subject-input"
        />
      </div>
      
      <div>
        <Label htmlFor="dpo-message">Messaggio</Label>
        <Textarea
          id="dpo-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          className="mt-2 resize-none"
          placeholder="Descrivi la tua richiesta in dettaglio..."
          data-testid="dpo-message-input"
        />
      </div>
      
      <PrivacyConsent
        checked={privacyAccepted}
        onChange={setPrivacyAccepted}
        testId="dpo-privacy-checkbox"
      />

      <HoneypotField value={honeypot} onChange={setHoneypot} idPrefix="dpo" />

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={isSubmitting || !privacyAccepted}
        data-testid="dpo-submit-btn"
      >
        {isSubmitting ? 'Invio in corso...' : 'Invia al DPO'}
        <Shield className="ml-2 w-4 h-4" />
      </Button>
    </form>
  );
};

// Job Application Form
const JobApplicationForm = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    message: '',
  });
  const [cvUrl, setCvUrl] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cvUrl) {
      toast.error('Per favore carica il tuo CV');
      return;
    }
    
    if (!privacyAccepted) {
      toast.error('Devi accettare l\'informativa sulla privacy per inviare la candidatura');
      return;
    }

    setIsSubmitting(true);

    try {
      await jobApplicationsAPI.create({ ...formData, website: honeypot }, cvUrl);
      toast.success('Candidatura inviata con successo!');
      setFormData({ first_name: '', last_name: '', email: '', phone: '', position: '', message: '' });
      setCvUrl('');
      setPrivacyAccepted(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio candidatura');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="job-form">
      <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 mb-6">
        <div className="flex items-start space-x-3">
          <Briefcase className="w-5 h-5 text-green-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-500">Lavora con noi</p>
            <p className="text-sm text-muted-foreground mt-1">
              Compila il form sottostante per candidarti. Ricordati di allegare il tuo CV aggiornato.
            </p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="first_name">Nome</Label>
          <Input
            id="first_name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
            className="mt-2"
            data-testid="job-firstname-input"
          />
        </div>
        <div>
          <Label htmlFor="last_name">Cognome</Label>
          <Input
            id="last_name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
            className="mt-2"
            data-testid="job-lastname-input"
          />
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="job-email">Email</Label>
          <Input
            id="job-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-2"
            data-testid="job-email-input"
          />
        </div>
        <div>
          <Label htmlFor="job-phone">Telefono</Label>
          <Input
            id="job-phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
            className="mt-2"
            data-testid="job-phone-input"
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="position">Posizione di interesse</Label>
        <Input
          id="position"
          name="position"
          value={formData.position}
          onChange={handleChange}
          required
          className="mt-2"
          placeholder="Es: Tecnico commerciale, Assistente amministrativo..."
          data-testid="job-position-input"
        />
      </div>

      <div>
        <Label>Curriculum Vitae</Label>
        <div className="mt-2">
          <ImageUpload
            value={cvUrl}
            onChange={setCvUrl}
            uploadType="cv"
            label="Carica il tuo CV (PDF, DOC, DOCX)"
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="job-message">Messaggio (opzionale)</Label>
        <Textarea
          id="job-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={4}
          className="mt-2 resize-none"
          placeholder="Presentati brevemente..."
          data-testid="job-message-input"
        />
      </div>
      
      {/* Privacy Checkbox */}
      <PrivacyConsent
        checked={privacyAccepted}
        onChange={setPrivacyAccepted}
        testId="job-privacy-checkbox"
        noticeHref="/compliance/privacy-candidati"
        note="* Campo obbligatorio per l'invio della candidatura"
      />

      <HoneypotField value={honeypot} onChange={setHoneypot} idPrefix="job" />

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={isSubmitting || !privacyAccepted}
        data-testid="job-submit-btn"
      >
        {isSubmitting ? 'Invio in corso...' : 'Invia Candidatura'}
        <Briefcase className="ml-2 w-4 h-4" />
      </Button>
    </form>
  );
};

// Whistleblowing Link Card
const WhistleblowingCard = () => {
  return (
    <div className="space-y-6" data-testid="whistleblowing-section">
      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-500">Segnalazioni Whistleblowing</p>
            <p className="text-sm text-muted-foreground mt-1">
              Per effettuare segnalazioni anonime ai sensi del D.Lgs. 24/2023, accedi alla piattaforma dedicata.
            </p>
          </div>
        </div>
      </div>

      <Card className="glass-card">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg">Piattaforma Whistleblowing</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Segnala in modo sicuro e, se lo desideri, anonimo eventuali illeciti o irregolarità.
              </p>
            </div>
            <div className="space-y-3">
              <Button asChild className="w-full">
                <Link to="/whistleblowing">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Effettua una Segnalazione
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/whistleblowing/track">
                  Traccia una Segnalazione
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const Contact = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'contact';

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.address'),
      content: t('contact.addressValue'),
    },
    {
      icon: Phone,
      title: 'P.IVA',
      content: 'P.IVA 01203470529 | SDI KRRH6B9',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'comunicazioni@gmed.it',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/2 w-96 h-96 bg-primary rounded-full blur-3xl animate-float"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-display font-bold text-5xl lg:text-6xl mb-6 text-white">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-gray-300">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Forms with Tabs */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="glass-card">
                <CardContent className="pt-6">
                  <Tabs defaultValue={defaultTab} className="space-y-6">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="contact" className="text-xs sm:text-sm" data-testid="tab-contact">
                        <Mail className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Contatti</span>
                      </TabsTrigger>
                      <TabsTrigger value="whistleblowing" className="text-xs sm:text-sm" data-testid="tab-whistleblowing">
                        <AlertTriangle className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Segnalazioni</span>
                      </TabsTrigger>
                      <TabsTrigger value="dpo" className="text-xs sm:text-sm" data-testid="tab-dpo">
                        <Shield className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">DPO</span>
                      </TabsTrigger>
                      <TabsTrigger value="jobs" className="text-xs sm:text-sm" data-testid="tab-jobs">
                        <Briefcase className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Lavora</span>
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="contact">
                      <ContactForm />
                    </TabsContent>

                    <TabsContent value="whistleblowing">
                      <WhistleblowingCard />
                    </TabsContent>

                    <TabsContent value="dpo">
                      <DPOContactForm />
                    </TabsContent>

                    <TabsContent value="jobs">
                      <JobApplicationForm />
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {contactInfo.map((info, index) => (
                <Card key={index} className="glass-card">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-lg mb-1 text-foreground">
                          {info.title}
                        </h3>
                        <p className="text-muted-foreground">{info.content}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Map */}
              <Card className="glass-card overflow-hidden">
                <div className="aspect-video bg-muted relative">
                  <iframe
                    title="Gmed Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d724.5614381475957!2d11.146065699197374!3d43.41369189999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132a3abc638173e9%3A0x26a52dcddf5ad9ce!2sS.da%20di%20Selvamaggio%2C%2019%2C%2053034%20Selvamaggio%20SI!5e0!3m2!1sit!2sit!4v1770622645880!5m2!1sit!2sit"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
